import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, ArrowUpRight, Play, Volume2, VolumeX, X } from "lucide-react";
import { CONTACT, ESAK_INSTAGRAM } from "../data/content";

const FEEDS = {
    avenue: {
        handle: "@uncoveravenue",
        url: CONTACT.instagram,
        testid: "insta-tab-avenue",
        posts: [
            { src: "/instagram/ua1.webp", link: "https://www.instagram.com/p/DbVuLtHGfMX/", alt: "Monsoon escape at Uncover Avenue" },
            { src: "/instagram/ua3.webp", link: "https://www.instagram.com/p/DZNEXtOOg19/", alt: "Green corners of the Avenue" },
            { src: "/instagram/ua4.webp", link: "https://www.instagram.com/p/DYMpYlEmXE2/", alt: "Pet-friendly vibes at the Avenue" },
            { src: "/instagram/ua5.webp", link: "https://www.instagram.com/p/DX67W61iIiT/", alt: "Gentle notes and green spaces" },
            { src: "/instagram/ua6.webp", link: "https://www.instagram.com/p/DZFiCqrmaWO/", alt: "Mood board, but make it a place" },
        ],
    },
    esak: {
        handle: "@esak.delhi",
        url: ESAK_INSTAGRAM,
        testid: "insta-tab-esak",
        posts: [
            { src: "/instagram/esak_r1.webp", video: "/instagram/esak_r1.mp4", link: "https://www.instagram.com/reel/DaiJfWdqMI4/", alt: "The forecast: 90% chance of coffee at Esak" },
            { src: "/instagram/esak_r2.webp", video: "/instagram/esak_r2.mp4", link: "https://www.instagram.com/reel/DOWPPldEiE_/", alt: "Come for the view, stay for the vibe at Esak" },
            { src: "/instagram/esak_r3.webp", video: "/instagram/esak_r3.mp4", link: "https://www.instagram.com/reel/DYcG_2sqNNY/", alt: "Summer drinks at Esak" },
            { src: "/instagram/esak_r4.webp", link: "https://www.instagram.com/p/DZ7dpLRGt3a/", alt: "New summer launches at Esak" },
            { src: "/instagram/esak_r5.webp", video: "/instagram/esak_r5.mp4", link: "https://www.instagram.com/reel/DOd3JBxErwx/", alt: "Modern love aesthetics at Esak" },
        ],
    },
};

const PostTile = ({ post, index }) => {
    const [mode, setMode] = useState("idle");
    const [muted, setMuted] = useState(true);
    const isReel = !!post.video;

    const videoSources = isReel ? (
        <>
            <source src={post.video} type="video/mp4" />
            <source src={post.video.replace(".mp4", ".webm")} type="video/webm" />
        </>
    ) : null;

    const baseCls = "relative rounded-3xl overflow-hidden aspect-square shadow-md hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 block text-left w-full";

    if (isReel && mode === "watch") {
        return (
            <div className={`${baseCls} bg-black shadow-xl`} data-testid={`reel-player-${index}`}>
                <video autoPlay loop muted={muted} playsInline className="w-full h-full object-cover">
                    {videoSources}
                </video>
                <button
                    onClick={() => setMuted(!muted)}
                    className="absolute bottom-3 right-3 rounded-full bg-black/60 backdrop-blur p-2.5 text-white hover:bg-black/80 transition-colors"
                    aria-label={muted ? "Unmute reel" : "Mute reel"}
                    data-testid={`reel-sound-toggle-${index}`}
                >
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
                <button
                    onClick={() => { setMode("idle"); setMuted(true); }}
                    className="absolute top-3 right-3 rounded-full bg-black/60 backdrop-blur p-2 text-white hover:bg-black/80 transition-colors"
                    aria-label="Close reel"
                    data-testid={`reel-close-${index}`}
                >
                    <X size={16} />
                </button>
            </div>
        );
    }

    return (
        <button
            onClick={() => (isReel ? setMode("watch") : window.open(post.link, "_blank", "noopener"))}
            onMouseEnter={() => isReel && setMode((m) => (m === "idle" ? "preview" : m))}
            onMouseLeave={() => setMode((m) => (m === "preview" ? "idle" : m))}
            className={`${baseCls} group`}
            data-testid={`insta-post-${index}`}
            aria-label={isReel ? `Play reel: ${post.alt}` : post.alt}
        >
            {isReel && mode === "preview" ? (
                <>
                    <video autoPlay loop muted playsInline className="w-full h-full object-cover" data-testid={`reel-preview-${index}`}>
                        {videoSources}
                    </video>
                    <span className="absolute bottom-3 left-3 rounded-full bg-black/55 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                        Tap for sound
                    </span>
                </>
            ) : (
                <>
                    <img src={post.src} alt={post.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-[#262055]/0 group-hover:bg-[#262055]/35 transition-colors duration-300 flex items-center justify-center">
                        {isReel ? (
                            <span className="rounded-full bg-white/90 p-4 opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-[transform,opacity] duration-300 shadow-lg">
                                <Play size={22} className="text-[#262055] fill-[#262055]" />
                            </span>
                        ) : (
                            <Instagram size={28} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        )}
                    </div>
                </>
            )}
            {isReel && (
                <span className="absolute top-3 left-3 rounded-full bg-black/55 backdrop-blur px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white" data-testid={`reel-badge-${index}`}>
                    Reel
                </span>
            )}
        </button>
    );
};

export const InstagramSection = () => {
    const [feed, setFeed] = useState("avenue");
    const active = FEEDS[feed];

    return (
        <section id="instagram" className="py-20 md:py-28 px-6 md:px-10" style={{ backgroundColor: "#FFF7C5" }} data-testid="instagram-section">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="flex flex-wrap items-end justify-between gap-6"
                >
                    <div>
                        <p className="font-serif-brand italic text-xl text-[#F0841B] mb-3">Fresh off the feed</p>
                        <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-[#262055]" data-testid="instagram-headline">
                            Follow the Avenue
                        </h2>
                        <p className="mt-3 text-sm text-[#262055]/55" data-testid="instagram-subcopy">Hover a reel for a silent preview, tap to watch with sound.</p>
                    </div>
                    <div className="flex gap-3" data-testid="insta-tabs">
                        {Object.entries(FEEDS).map(([key, f]) => (
                            <button
                                key={key}
                                onClick={() => setFeed(key)}
                                data-testid={f.testid}
                                className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-200 ${
                                    feed === key ? "bg-[#262055] text-white shadow-lg" : "bg-white text-[#262055]/60 hover:text-[#262055]"
                                }`}
                            >
                                {f.handle}
                            </button>
                        ))}
                    </div>
                </motion.div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={feed}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35 }}
                        className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
                        data-testid="insta-grid"
                    >
                        {active.posts.map((p, i) => (
                            <PostTile key={p.src} post={p} index={i} />
                        ))}
                        <a
                            href={active.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-3xl aspect-square flex flex-col items-center justify-center gap-3 text-white transition-[transform,filter] duration-300 hover:-translate-y-1 hover:brightness-110"
                            style={{ background: feed === "esak" ? "#E50046" : "#00958C" }}
                            data-testid="insta-follow-card"
                        >
                            <Instagram size={32} />
                            <span className="text-sm font-bold">Follow {active.handle}</span>
                            <ArrowUpRight size={18} />
                        </a>
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
};
